import React, { useState, useEffect, useCallback, useMemo } from "react";
import API_URL from "../config/api";

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const BACKEND_URL = API_URL;
  const apiKey = process.env.REACT_APP_BACKEND_API_KEY || "";
  const authHeaders = useMemo(
    () => (apiKey ? { "X-API-Key": apiKey } : {}),
    [apiKey]
  );

  // Fetch scraping statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/scraping/stats`, {
        headers: { ...authHeaders },
      });
      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      } else {
        setMessage("Failed to fetch statistics");
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      setMessage("Error: Backend server may not be running");
    }
  }, [BACKEND_URL, authHeaders]);

  // Run manual scraping
  const runScraping = async (endpoint = "/api/scraping/run") => {
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ maxJobs: 20 }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        // Refresh stats after a delay
        setTimeout(fetchStats, 5000);
      } else {
        setMessage(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error("Error running scraping:", error);
      setMessage("Error: Could not connect to backend server");
    } finally {
      setLoading(false);
    }
  };

  // Test backend connection
  const testBackend = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/scraping/test`, {
        headers: { ...authHeaders },
      });
      const data = await response.json();

      if (data.success) {
        setMessage("✅ Backend connection successful");
        fetchStats();
      } else {
        setMessage(`❌ Backend test failed: ${data.error}`);
      }
    } catch (error) {
      setMessage(
        "❌ Cannot connect to Railway backend. Check if deployment is running."
      );
    }
  }, [BACKEND_URL, fetchStats, authHeaders]);

  useEffect(() => {
    testBackend();
  }, [testBackend]);

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>🔧 Job Scraping Admin Panel</h2>

      {/* Connection Status */}
      <div style={styles.section}>
        <h3>Backend Connection</h3>
        <button onClick={testBackend} style={styles.button}>
          Test Connection
        </button>
        {message && <p style={styles.message}>{message}</p>}
      </div>

      {/* Statistics */}
      <div style={styles.section}>
        <h3>📊 Statistics</h3>
        <button onClick={fetchStats} style={styles.button}>
          Refresh Stats
        </button>

        {stats && (
          <div style={styles.stats}>
            <p>
              <strong>Total Jobs in Database:</strong>{" "}
              {stats.totalJobsInDatabase}
            </p>
            <p>
              <strong>Jobs Added (Last 24h):</strong> {stats.totalJobs}
            </p>
            <p>
              <strong>Last Scraped:</strong>{" "}
              {new Date(stats.lastScraped).toLocaleString()}
            </p>

            {stats.sourceBreakdown &&
              Object.keys(stats.sourceBreakdown).length > 0 && (
                <div>
                  <p>
                    <strong>Source Breakdown (Last 24h):</strong>
                  </p>
                  <ul style={styles.sourceList}>
                    {Object.entries(stats.sourceBreakdown).map(
                      ([source, count]) => (
                        <li key={source}>
                          {source}: {count} jobs
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Manual Scraping Controls */}
      <div style={styles.section}>
        <h3>🚀 Manual Job Search</h3>
        <p style={styles.description}>
          Search for entry-level career jobs using legal APIs (Adzuna & USAJobs)
        </p>

        <div style={styles.buttonGroup}>
          <button
            onClick={() => runScraping("/api/scraping/adzuna/entry-level")}
            disabled={loading}
            style={loading ? styles.buttonDisabled : styles.primaryButton}
          >
            {loading ? "Searching..." : "🔍 Search Adzuna (Career Jobs)"}
          </button>

          <button
            onClick={() => runScraping("/api/scraping/usajobs/entry-level")}
            disabled={loading}
            style={loading ? styles.buttonDisabled : styles.button}
          >
            {loading ? "Searching..." : "🏛️ Search USAJobs (Government)"}
          </button>

          <button
            onClick={() => runScraping("/api/scraping/run")}
            disabled={loading}
            style={loading ? styles.buttonDisabled : styles.secondaryButton}
          >
            {loading ? "Running..." : "🚀 Run All Scrapers"}
          </button>
        </div>

        <div style={styles.infoBox}>
          <p style={styles.infoTitle}>📊 What Gets Searched:</p>
          <ul style={styles.infoList}>
            <li><strong>Adzuna API:</strong> Tech, Engineering, Business, Finance, Data Science, Marketing</li>
            <li><strong>USAJobs API:</strong> Government positions (GS-5, GS-7, GS-9 grades + Graduate programs)</li>
            <li><strong>Validation:</strong> Section-based entry-level detection filters out jobs with experience requirements</li>
          </ul>
        </div>

        <p style={styles.note}>
          ⚠️ Searches run in background. Results appear in a few minutes. Check stats to see progress.
        </p>
      </div>

      {/* Instructions */}
      <div style={styles.section}>
        <h3>📋 How It Works</h3>
        <ol style={styles.instructions}>
          <li>
            <strong>Backend:</strong> Running 24/7 on Railway at{" "}
            <code>{BACKEND_URL}</code>
          </li>
          <li>
            <strong>Automatic Scraping:</strong> Runs every 6 hours automatically
          </li>
          <li>
            <strong>Manual Search:</strong> Use buttons above to trigger immediate job search
          </li>
          <li>
            <strong>Validation:</strong> Only true entry-level positions (no experience required) are saved
          </li>
          <li>
            <strong>Career Focus:</strong> Searches professional fields (tech, engineering, business, finance, etc.)
          </li>
        </ol>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "800px",
    margin: "20px auto",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  title: {
    textAlign: "center",
    color: "#333",
    marginBottom: "30px",
  },
  section: {
    backgroundColor: "white",
    padding: "20px",
    marginBottom: "20px",
    borderRadius: "6px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  button: {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "10px 16px",
    borderRadius: "4px",
    cursor: "pointer",
    marginRight: "10px",
    marginBottom: "10px",
  },
  primaryButton: {
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    padding: "12px 20px",
    borderRadius: "4px",
    cursor: "pointer",
    marginRight: "10px",
    marginBottom: "10px",
    fontWeight: "bold",
  },
  secondaryButton: {
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    padding: "12px 20px",
    borderRadius: "4px",
    cursor: "pointer",
    marginRight: "10px",
    marginBottom: "10px",
  },
  buttonDisabled: {
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    padding: "10px 16px",
    borderRadius: "4px",
    cursor: "not-allowed",
    marginRight: "10px",
    marginBottom: "10px",
  },
  buttonGroup: {
    marginBottom: "15px",
  },
  message: {
    marginTop: "10px",
    padding: "10px",
    backgroundColor: "#e9ecef",
    borderRadius: "4px",
    border: "1px solid #dee2e6",
  },
  stats: {
    marginTop: "15px",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "4px",
    border: "1px solid #dee2e6",
  },
  sourceList: {
    margin: "5px 0",
    paddingLeft: "20px",
  },
  note: {
    fontSize: "14px",
    color: "#6c757d",
    fontStyle: "italic",
  },
  instructions: {
    paddingLeft: "20px",
    lineHeight: "1.6",
  },
  description: {
    color: "#6c757d",
    fontSize: "14px",
    marginBottom: "15px",
  },
  infoBox: {
    backgroundColor: "#e7f3ff",
    border: "1px solid #b3d9ff",
    borderRadius: "4px",
    padding: "15px",
    marginTop: "15px",
    marginBottom: "15px",
  },
  infoTitle: {
    fontWeight: "bold",
    marginBottom: "10px",
    color: "#0066cc",
  },
  infoList: {
    margin: "0",
    paddingLeft: "20px",
    lineHeight: "1.8",
  },
};

export default AdminPanel;
